import { useRequest } from 'features/request/useRequest';
import type { ChapterEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { getWhereFromFilter } from '../helpers';
import { getWorkOrderChangePriceList } from '../WorkOrderChangeQueries';

export const useWorkOrderChangePriceList = ({
    chapter,
    workOrderChangeId,
    workOrderObjectId,
    workOrderChangeObjectId,
    gridApiRef,
}: {
    chapter: ChapterEnum;
    workOrderChangeId: number;
    workOrderObjectId?: number | null;
    workOrderChangeObjectId?: number | null;
    gridApiRef: any;
}) => {
    const [filtersWhere, setFiltersWhere] = useState<Record<string, any> | undefined>(undefined);

    const variables = useMemo(() => {
        if (!workOrderObjectId && !workOrderChangeObjectId) {
            return undefined;
        }

        return {
            workOrderChangeId,
            workOrderObjectId,
            workOrderChangeObjectId,
            chapter,
            where: filtersWhere,
        };
    }, [workOrderChangeId, workOrderObjectId, workOrderChangeObjectId, chapter, filtersWhere]);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues, fullDataSet }) => {
        setFiltersWhere((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getWhereFromFilter(fieldName, checkedValues, fullDataSet),
            },
        }));
    }, []);

    const [workOrderChangePriceData, , , workOrderChangePriceRequest] = useRequest({
        requestId: 'workOrderChangePrice',
        query: getWorkOrderChangePriceList,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'workOrderChangePrice',
        requestFn: workOrderChangePriceRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (workOrderChangePriceData?.uerWorkOrderChangePriceList?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                workOrderChangePriceData.uerWorkOrderChangePriceList.items,
                workOrderChangePriceData.uerWorkOrderChangePriceList?.totalCount,
            );

            gridApiRef?.current?.api?.refreshHeader();
        }
    }, [
        getRowParams,
        gridApiRef,
        workOrderChangePriceData?.uerWorkOrderChangePriceList?.items,
        workOrderChangePriceData?.uerWorkOrderChangePriceList?.totalCount,
    ]);

    usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `${chapter}_${workOrderObjectId}_${workOrderChangeObjectId}`,
                    getDefaultFilterCleaner({ changeFilterHandler, setFiltersWhere }),
                ),
            }),
            [changeFilterHandler, chapter, workOrderChangeObjectId, workOrderObjectId],
        ),
    );

    const totalItems = useMemo(
        () => workOrderChangePriceData?.uerWorkOrderChangePriceList?.totalCount,
        [workOrderChangePriceData?.uerWorkOrderChangePriceList?.totalCount],
    );

    const fetchedItems = useMemo(() => {
        if (!getRowParams?.current?.endRow || !totalItems) {
            return 0;
        }

        if (getRowParams?.current?.endRow > totalItems) {
            return totalItems;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [totalItems, getRowParams?.current?.endRow]);

    return { dataSource, totalItems, fetchedItems, changeFilterHandler };
};
